import type { CaseData, EditorBlock, MemberDashboardData } from '../types'
import { buildPreviewCase } from './previewMemberData'

async function api<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, { credentials: 'include', ...init, headers: { Accept: 'application/json', ...init.headers } })
  const body = await response.json().catch(() => ({})) as T & { error?: string }
  if (!response.ok) throw new Error(body.error || '요청을 처리하지 못했습니다.')
  return body
}

export async function loadMemberDashboard(signal?: AbortSignal) {
  return api<MemberDashboardData>('/api/member/dashboard', { signal })
}

export async function loadMemberCase(caseId: string, signal?: AbortSignal) {
  const body = await api<{ case: CaseData }>(`/api/member/case?case=${encodeURIComponent(caseId)}`, { signal })
  return body.case
}

export async function updateCaseDetails(input: { caseId: string; clinicVisitDate: string }, csrf: string) {
  return api<{ ok: true; updatedAt: string }>('/api/member/case', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(input),
  })
}

export async function createPet(input: { name: string; species: string; sex: string; estimatedBirth?: string }, csrf: string) {
  return api<{ petId: string; caseId: string }>('/api/member/pets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(input),
  })
}

export interface PetProfileInput {
  petId: string
  caseId: string
  name: string
  species: string
  sex: string
  estimatedBirth: string
  adoptionDate: string
  adoptionSource: string
  registration: string
  insurance: string
  firstCondition: string
  intakeStatement: string
  appetite: string
  feeding: string
  stool: string
  personality: string
}

export async function updatePetProfile(input: PetProfileInput, csrf: string) {
  return api<{ ok: true; updatedAt: string }>('/api/member/pets', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(input),
  })
}

export interface RecordInput {
  caseId: string
  petIds: string[]
  category: 'symptom' | 'measurement' | 'medication' | 'observation' | 'visit'
  occurredAt: string
  title: string
  detail: string
  severity: 'critical' | 'attention' | 'stable' | 'info'
  blocks: EditorBlock[]
}

export async function createMemberRecord(input: RecordInput, csrf: string) {
  return api<{ eventId: string; updatedAt: string }>('/api/member/records', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(input),
  })
}

export async function updateMemberRecord(input: RecordInput & { eventId: string }, csrf: string) {
  return api<{ ok: true; updatedAt: string }>('/api/member/records', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(input),
  })
}

export async function deleteMemberRecord(caseId: string, eventId: string, csrf: string) {
  return api<{ ok: true; updatedAt: string }>('/api/member/records', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ caseId, eventId }),
  })
}

export async function createGuardianQuestion(input: { caseId: string; petId: string; title: string; detail: string }, csrf: string) {
  return api<{ id: string; updatedAt: string }>('/api/member/questions', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf }, body: JSON.stringify(input) })
}

export async function updateGuardianQuestion(input: { caseId: string; id: string; petIds: string[]; title: string; detail: string }, csrf: string) {
  return api<{ ok: true; updatedAt: string }>('/api/member/questions', { method: 'PATCH', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf }, body: JSON.stringify(input) })
}

export async function deleteGuardianQuestion(caseId: string, id: string, csrf: string) {
  return api<{ ok: true; updatedAt: string }>('/api/member/questions', { method: 'DELETE', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf }, body: JSON.stringify({ caseId, id }) })
}

export async function uploadMemberFile(form: FormData, csrf: string, onProgress?: (value: number) => void) {
  onProgress?.(12)
  const result = await api<{ fileId: string; assetId: string; name: string; folderPath: string }>('/api/member/upload', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrf },
    body: form,
  })
  onProgress?.(100)
  return result
}

export async function renameMemberFile(input: { caseId: string; assetId: string; kind: 'media' | 'document'; title: string }, csrf: string) {
  return api<{ ok: true; updatedAt: string }>('/api/member/file', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(input),
  })
}

export async function deleteMemberFile(input: { caseId: string; assetId: string; kind: 'media' | 'document' }, csrf: string) {
  return api<{ ok: true }>('/api/member/file', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(input),
  })
}

export async function createShareLink(caseId: string, expiresInDays: number, csrf: string) {
  return api<{ url: string; expiresAt: string }>('/api/member/shares', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ caseId, expiresInDays }),
  })
}

export async function revokeShareLink(shareId: string, csrf: string) {
  return api<{ ok: true }>('/api/member/shares', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ shareId }),
  })
}

export async function updateMemberProfile(input: { name: string; phone: string; address: string }, csrf: string) {
  return api<{ ok: true }>('/api/member/profile', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(input),
  })
}

export async function requestEmailChange(email: string, csrf: string) {
  return api<{ status: 'verification_sent' }>('/api/member/email-change', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ email }),
  })
}

export async function changePassword(currentPassword: string, nextPassword: string, csrf: string) {
  return api<{ ok: true }>('/api/member/password', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ currentPassword, nextPassword }),
  })
}

export async function loadSharedCase(token: string, signal?: AbortSignal) {
  if (import.meta.env.DEV && token === 'preview') return { case: buildPreviewCase(), expiresAt: new Date(Date.now() + 7 * 86_400_000).toISOString() }
  const body = await api<{ case: CaseData; expiresAt: string }>(`/api/shared-case?token=${encodeURIComponent(token)}`, { signal })
  return body
}
